import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import ButtonsFixed, { SpaceButtons } from '@ui/buttons/ButtonsFixed'
import { prescriptionCreateTag } from '@ca/common'
import EmptyResult from '@ui/components/EmptyResult'
import Page from '@shell/Page'
import { useResolvedPd } from '@order/PdSection'
import Header from '@ui/elements/Header'
import PrescriptionsModal from '@order/modals/Prescriptions/PrescriptionsModal'
import {
  usePrescriptionById,
  usePrescriptionWithDefaults,
} from '@order/hooks/usePrescription'
import { useSelectPrescription } from '@order/hooks/useSelectPrescription'
import useStartOrderConfig from '@order/hooks/useStartOrderConfig'
import { trpc } from 'api'
import { router } from 'expo-router'
import Analytics from 'hooks/analytics/Analytics'
import { useCustomer } from 'hooks/useSession'
import Content from './Content'
import ConfigDebugModal from 'features/debug/ConfigDebugModal'

export default function VisionValues() {
  const { t } = useTranslation(['ui', 'vision'])
  const text = 'vision:values'

  const [openModal, setOpenModal] = useState(false)

  const customer = useCustomer()
  const { startConfig } = useStartOrderConfig()
  const pd = useResolvedPd()
  const { select: selectPrescription, selectedId: selectedPrescriptionId } =
    useSelectPrescription()
  const { data: prescription, isLoading: isLoadingPrescription } =
    usePrescriptionById(selectedPrescriptionId)

  const isPdValid = !!pd

  const { prescriptions, isLoading, hasPrescriptions } =
    usePrescriptionWithDefaults()
  const utils = trpc.useUtils()

  useEffect(() => {
    if (prescriptions && !selectedPrescriptionId) {
      const newPrescription = prescriptions[0]
      newPrescription && selectPrescription(newPrescription)
    }
  }, [prescriptions, selectedPrescriptionId, selectPrescription])

  const createPrescription = trpc.customer.prescriptions.create.useMutation({
    onSuccess: (res) => {
      utils.customer.prescriptions.list.invalidate()
      Analytics.logEvent('prescription_external_pd', {
        prescriptionId: `${res.id}`,
        optometrist: res.optometrist_name || '',
      })
      selectPrescription(res)
      startConfig()
    },
    // On error we can just proceed as is
    onError: startConfig,
  })

  const start = () => {
    console.log('start', { isPdValid })
    if (!isPdValid) {
      return
    }
    const needsCreate = prescription?.l_pd === 0 || prescription?.r_pd === 0

    console.log('needsCreate', { needsCreate })
    if (!needsCreate || !prescription?.optometrist || !customer) {
      startConfig()
      return
    }
    const { create_date, ...data } = prescription
    // Assume we have valid pd at this point as the action is disabled otherwise
    createPrescription.mutate({
      data: {
        ...data,
        ...pd,
        remark: `[PD]\n${prescription.remark || ''}`,
        name: prescriptionCreateTag.pdUpdated,
      },

      customerId: `${customer.id}`,
    })
  }

  return (
    <>
      <Page stage="vision" validateStaff>
        <Header
          title={t(`${text}.header.title`)}
          desc={t(`${text}.header.desc`)}
        />
        {!hasPrescriptions && !isLoading && (
          <View className="px-8">
            <EmptyResult
              title={t(`${text}.empty.result.title`)}
              desc={t(`${text}.empty.result.desc`)}
              button={{
                label: t(`ui:addPrescription`),
                onPress: () =>
                  router.navigate({
                    pathname: '/vision/prescription/create',
                    params: { order: '1' },
                  }),
              }}
            />
          </View>
        )}
        <Content
          isLoading={isLoadingPrescription} // WARN: do not include isLoading here - unnecessary loading state
          prescription={prescription}
        />
        <View className="h-64 w-full rounded-2xl" />
        <SpaceButtons />
      </Page>
      <ButtonsFixed
        hidden={isLoading || !prescription}
        buttons={[
          {
            variant: 'outlined',
            label: t('ui:prescriptions'),
            onPress: () => setOpenModal(true),
          },
          {
            variant: 'filled',
            label: t('ui:nextStep'),
            disabled: !isPdValid,
            iconEnd: 'arrow-narrow-right',
            loading: createPrescription.isPending,
            onPress: start,
          },
        ]}
      />
      <PrescriptionsModal open={openModal} close={() => setOpenModal(false)} />
      <ConfigDebugModal />
    </>
  )
}
